import React from 'react';
import { GoogleMap, Libraries, useJsApiLoader } from '@react-google-maps/api';
import { useCurrentPosition } from 'src/hooks/useCurrentPosition';
import { getLocalMerchantId } from 'components/settings/localStore';

interface Point {
    lat: number;
    lng: number;
}

export interface IPoint extends Point {
    markerName: string;
}

interface GMapProps {
    center: Point;
    width: number;
    className?: string;
    height?: number;
    markers?: IPoint[];
    options?: google.maps.MapOptions;
    zoom?: number;
    useCustomPin?: boolean;
    boundAll?: boolean;
    boundTo?: Point;
    onMapLoad?: (map: google.maps.Map) => void;
}

function fitMapBounds(lat: number, lng: number, width: number, map: google.maps.Map) {
    if (lat && lng && width && map) {
        map.fitBounds(
            new google.maps.LatLngBounds(
                new google.maps.LatLng(lat, lng - width / 2),
                new google.maps.LatLng(lat, lng + width / 2)
            )
        );
    }
}

const MAP_ID = 'Mui-google-map';
const GOOGLE_MAPS_LIBRARIES: Libraries = ['marker'];

export const MuiMap: React.FC<React.PropsWithChildren<GMapProps>> = props => {
    const { center, width, className, height, boundAll, options, markers, useCustomPin, onMapLoad, ...rest } =
        props;
    const { isLoaded } = useJsApiLoader({
        id: MAP_ID,
        googleMapsApiKey: process.env.GOOGLE_API_KEY,
        libraries: GOOGLE_MAPS_LIBRARIES
    });
    const { lat, lng } = center;
    const [map, setMap] = React.useState(null as null | google.maps.Map);
    const { position: devicePosition } = useCurrentPosition();
    const [isCustomPinAvailable, setIsCustomPinAvailable] = React.useState(false);
    const [customPinDimensions, setCustomPinDimensions] = React.useState([32, 32]);
    const merchantId = getLocalMerchantId();
    const customPinPath = React.useMemo(
        () => `${process.env.MEDIA_URL}/tenants/${merchantId}/app_assets/map_pin`,
        [merchantId]
    );
    const [tempMapMarkers, setTempMapMarkers] = React.useState<google.maps.marker.AdvancedMarkerElement[]>(
        []
    );
    React.useEffect(() => {
        if (map) {
            if (markers && boundAll) {
                const bounds = new google.maps.LatLngBounds();

                markers.map(marker => {
                    const position = new google.maps.LatLng(marker.lat, marker.lng);

                    bounds.extend(position);
                });
                map.fitBounds(bounds);
            } else if (props.boundTo) {
                const bounds = new google.maps.LatLngBounds();
                bounds.extend({ lat, lng });
                bounds.extend({ lat: props.boundTo.lat, lng: props.boundTo.lng });
                map.fitBounds(bounds);
            } else {
                fitMapBounds(lat, lng, width, map);
            }
        }
    }, [boundAll, lat, lng, map, markers, props.boundTo, width]);

    React.useEffect(() => {
        if (map && isLoaded) {
            // create markers
            if (devicePosition) {
                const currentLocationImg = document.createElement('img');
                currentLocationImg.src = 'current-location.svg';
                // eslint-disable-next-line no-new
                new google.maps.marker.AdvancedMarkerElement({
                    map,
                    position: { lat: devicePosition.coords.latitude, lng: devicePosition.coords.longitude },
                    content: currentLocationImg
                });
            }
            if (Array.isArray(markers)) {
                markers.forEach(item => {
                    if (useCustomPin && isCustomPinAvailable) {
                        const customMarkerImage = document.createElement('img');
                        const [width, height] = customPinDimensions;
                        customMarkerImage.width = width;
                        customMarkerImage.height = height;
                        customMarkerImage.src = customPinPath;
                        // eslint-disable-next-line no-new
                        new google.maps.marker.AdvancedMarkerElement({
                            map,
                            position: { lat: item.lat, lng: item.lng },
                            content: customMarkerImage
                        });
                    } else {
                        const tempMarker = new google.maps.marker.AdvancedMarkerElement({
                            map,
                            position: { lat: item.lat, lng: item.lng },
                            content: new google.maps.marker.PinElement().element
                        });
                        setTempMapMarkers(prev => [...prev, tempMarker]);
                    }
                });
            }
        }
    }, [
        customPinPath,
        customPinDimensions,
        devicePosition,
        isCustomPinAvailable,
        isLoaded,
        map,
        markers,
        useCustomPin
    ]);

    // Remove temp map markers when custom pin is available, so we don't have duplicate markers
    React.useEffect(() => {
        if (map && tempMapMarkers.length > 0 && isCustomPinAvailable) {
            tempMapMarkers.forEach(marker => {
                marker.map = null;
            });
        }
    }, [map, tempMapMarkers, isCustomPinAvailable]);

    const handleMapLoad = React.useCallback(
        (loadedMap: google.maps.Map) => {
            setMap(loadedMap);
            fitMapBounds(lat, lng, width, loadedMap);
            if (onMapLoad) {
                onMapLoad(loadedMap);
            }
        },
        [lat, lng, onMapLoad, width]
    );

    // Check if merchant has uploaded custom pin
    React.useEffect(() => {
        const img = new Image();
        img.onload = () => {
            const biggestDimension = img.width > img.height ? img.width : img.height;
            const scale = biggestDimension > 100 ? 45 / biggestDimension : 1;
            setCustomPinDimensions([img.width * scale, img.height * scale]);
            setIsCustomPinAvailable(true);
        };

        img.src = customPinPath;
    }, [customPinPath, merchantId]);

    const mapOptions: google.maps.MapOptions = {
        disableDefaultUI: false,
        draggable: true,
        gestureHandling: 'auto',
        fullscreenControl: false,
        mapId: MAP_ID,
        ...options
    };

    if (!isLoaded) {
        return null;
    }

    return (
        <GoogleMap
            id={MAP_ID}
            clickableIcons={false}
            center={center}
            onLoad={handleMapLoad}
            mapContainerClassName={className}
            mapContainerStyle={{}}
            options={mapOptions}
            {...rest}
        >
            <div style={{ height: height || 'inherit', width: '100%' }} />
        </GoogleMap>
    );
};
